import React, { useContext, useState, useEffect } from "react";
import { Context } from "../../../Context";
import { UserAdministrationNewView } from "../../views";
import { SimpleLoading, ModalUtil } from "../../pures";
import { LocalStorageService, UserService } from "../../../services";
import { useTranslation } from "react-i18next";
import "moment/locale/es";

/**
 * @class UserAdministrationNewAction
 * @extends {React.Component}
 * @status no delete
 */
const UserAdministrationNewAction = (props) => {
  const [initialLoading, setInitialLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [selectedUser, setSelectedUser] = useState({})
  const [clientAssistants, setClientAssistants] = useState([])
  const [loadingClientAssistants, setLoadingClientAssistants] = useState(false)

  const url = process.env.REACT_APP_ETICKET_API;
  const localStorageService = new LocalStorageService();
  const userService = new UserService(url)
  const token = localStorageService.getLocalStorageItem("token").idToken
  const modalUtil = new ModalUtil()
  const { t: tCommon } = useTranslation()
  const { t } = useTranslation("user-administration")

  const updateUserStatus = async (userStatus, email) => {
    modalUtil.showModal('loading', tCommon("modal.pure.loading.message"), tCommon("modal.pure.loading.title"))

    try {
      const response = await userService.updateUserStatus(token, { email, status: userStatus })
      if (response.statusCode === 200) {
        await modalUtil.showModal('success', t("updateUserStatusSuccess"), tCommon("modal.pure.title.success"))
        getInfoUsers()
      } else {
        throw new Error(t("updateUserStatusError"))
      }
    } catch (e) {
      console.log(e)
      modalUtil.showModal('error', e.message || 'Error al actualizar usuario', tCommon("modal.pure.title.error"))
    }
  }

  const createNewUser = async (email, roleId, assistantIds = []) => {
    modalUtil.showModal('loading', tCommon("modal.pure.loading.message"), tCommon("modal.pure.loading.title"))

    try {
      const newUser = await userService.createNewUserCollaborator(token, email, roleId, assistantIds)
      if (newUser.statusCode === 200) {
        await modalUtil.showModal('success', t('createUserSuccess'), tCommon("modal.pure.title.success"))
        getInfoUsers()
      } else if (newUser.statusCode === -2) {
        throw new Error(t("userAlreadyRegistered"))
      } else {
        throw new Error(t(newUser.message) || newUser.message || t("createUserError"))
      }
    } catch (e) {
      console.log(e)
      modalUtil.showModal('error', e.message || t('createUserError'), tCommon("modal.pure.title.error"))
    }
  }

  /**
   * Reenvía el correo de invitación para que un usuario que no terminó su onboarding lo reintente
   */
  const resendUserInvitation = async (email) => {
    modalUtil.showModal('loading', tCommon("modal.pure.loading.message"), tCommon("modal.pure.loading.title"))

    try {
      const response = await userService.resendUserInvitation(token, email)
      if (response.statusCode === 200) {
        await modalUtil.showModal('success', t("resendInvitationSuccess"), tCommon("modal.pure.title.success"))
      } else {
        throw new Error(t(response.message) || response.message || t("resendInvitationError"))
      }
    } catch (e) {
      console.error(`An error occurred in event resendUserInvitation:: ${e}`)
      modalUtil.showModal('error', e.message || t("resendInvitationError"), tCommon("modal.pure.title.error"))
    }
  }

  const updateUserRoles = async (roleIds) => {
    modalUtil.showModal('loading', tCommon("modal.pure.loading.message"), tCommon("modal.pure.loading.title"))

    try {
      const response = await userService.updateUserRoles(token, { userId: selectedUser.id, roles: roleIds })
      if (response.statusCode === 200) {
        await modalUtil.showModal('success', t("updateRolesSuccess"), tCommon("modal.pure.title.success"))
        getInfoUsers()
      } else if (response.statusCode === -2) {
        throw new Error(t("cannotDeleteAdmins"))
      } else {
        throw new Error(t("updateRolesError"))
      }
    } catch (e) {
      console.log(e)
      modalUtil.showModal('error', e.message || t("updateRolesError"), tCommon("modal.pure.title.error"))
      throw e
    }
  }

  const updateUserPermissions = async (permissionIds) => {
    modalUtil.showModal('loading', tCommon("modal.pure.loading.message"), tCommon("modal.pure.loading.title"))

    try {
      const response = await userService.updateUserPermissions(token, {
        userId: selectedUser.id,
        permissionIds
      })
      if (response.statusCode === 200) {
        await modalUtil.showModal('success', t("updatePermissionsSuccess"), tCommon("modal.pure.title.success"))
        getInfoUsers()
      } else if (response.statusCode === -2) {
        throw new Error(t("cannotModifyAdminPermissions"))
      } else {
        throw new Error(t("updatePermissionsError"))
      }
    } catch (e) {
      console.log(e)
      modalUtil.showModal('error', e.message || t("updatePermissionsError"), tCommon("modal.pure.title.error"))
      throw e
    }
  }

  const getInfoUsers = async () => {
    let usersCollaboratorAdmin = await userService.getUsersCollaboratorAdmin(token)
    console.log('Users from service:', usersCollaboratorAdmin)
    if (usersCollaboratorAdmin.length === 0) {
      setUsers([])
      return
    }

    const enrichedUsers = usersCollaboratorAdmin.map(user => {
      return {
        ...user,
        status: user.active ? t("active") : t("inactive"),
        name: `${user.firstName} ${user.firstLastName} ${user.secondLastName} `,
        permissions: user.permissions || []
      }
    })
    
    setUsers(enrichedUsers)

    if (selectedUser?.id) {
      const updatedUser = enrichedUsers.find(u => u.id === selectedUser.id)
      if (updatedUser) {
        setSelectedUser(updatedUser)
      } else {
        // if the user is no longer in the list, clear the selection
        setSelectedUser({})
      }
    }
  }

  /**
   * Asistentes del cliente, usados para elegir accesos al invitar a un usuario nuevo
   * (los endpoints de asistentes disponibles/asignados requieren un userId que aún no existe)
   */
  const getClientAssistants = async () => {
    try {
      setLoadingClientAssistants(true)
      const accessToken = localStorageService.getLocalStorageItem("token")?.accessToken
      const clientId = localStorageService.getLocalStorageItem("user")?.userMetaData?.userA3Cloud?.clientId
      if (!clientId) return

      const assistants = await userService.getAssistants(clientId, accessToken)
      setClientAssistants((assistants || []).map(bot => ({ ...bot, name: bot.chatbotName || bot.name })))
    } catch (e) {
      console.error(`An error occurred in event getClientAssistants:: ${e}`)
    } finally {
      setLoadingClientAssistants(false)
    }
  }

  const getInfoRoles = async () => {
    const data = await userService.getRolesAssignView(token)
    setRoles(data)
  }

  const getInfo = async (includeRoles = false) => {
    try {
      setInitialLoading(true)
      await getInfoUsers()
      if (includeRoles) await getInfoRoles()
      await getClientAssistants()
      setInitialLoading(false)
    } catch (e) {
      console.log(e)
      modalUtil.showModal('error', e.message || t("loadingError"), tCommon("modal.pure.title.error"))
    }
  }

  const getAvailableAssistants = async () => {
    try {
      const clientId = selectedUser.clientId
      if (!clientId) throw new Error("Client ID not found for user")
      
      const availableBots = await userService.getAvailableAssistants(selectedUser.id, clientId)
      
      const mappedBots = availableBots.map(bot => {
        return {
          ...bot,
          name: bot.chatbotName
        }
      })
      
      return mappedBots || []
    } catch (e) {
      console.error('ACTION AVAILABLE ERROR:', e)
      console.error('ACTION AVAILABLE ERROR STACK:', e.stack)
      throw e
    }
  }

  const getAssignedAssistants = async () => {
    try {
      const assigned = await userService.getAssignedAssistants(selectedUser.id)
      const mappedAssigned = assigned.map(bot => {
        return {
          ...bot,
          name: bot.chatbotName || bot.name
        }
      })
      
      return mappedAssigned || []
    } catch (e) {
      console.error('ACTION ASSIGNED ERROR:', e)
      console.error('ACTION ASSIGNED ERROR STACK:', e.stack)
      throw e
    }
  }

  const assignAssistant = async (assistantIds) => {
    modalUtil.showModal('loading', tCommon("modal.pure.loading.message"), tCommon("modal.pure.loading.title"))

    try {
      const response = await userService.assignAssistant(selectedUser.id, assistantIds)
      // Cierra el modal de loading primero
      await new Promise(resolve => setTimeout(resolve, 300))
      
      if (response?.statusCode === 200 || response?.data?.statusCode === 200) {
        await modalUtil.showModal('success', t("assignAssistantsSuccess"), tCommon("modal.pure.title.success"))
        await getInfoUsers()
        return true
      } else {
        console.error('Response status:', response?.statusCode)
        throw new Error(response?.message || t("assignAssistantsError"))
      }
    } catch (e) {
      console.error('Error in assignAssistant:', e)
      console.error('Full error:', JSON.stringify(e))
      await new Promise(resolve => setTimeout(resolve, 300))
      await modalUtil.showModal('error', e.message || t("assignAssistantsError"), tCommon("modal.pure.title.error"))
      return false
    }
  }

  useEffect(() => {
    getInfo(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

    return (
    <>
      {
        initialLoading
          ? <div className="loading">
            <SimpleLoading />
          </div>
          : <UserAdministrationNewView
            t={t}
            users={users}
            setData={setUsers}
            roles={roles}
            initialLoading={initialLoading}
            createNewUser={createNewUser}
            updateUserStatus={updateUserStatus}
            resendUserInvitation={resendUserInvitation}
            updateUserRoles={updateUserRoles}
            updateUserPermissions={updateUserPermissions}
            getAvailableAssistants={getAvailableAssistants}
            getAssignedAssistants={getAssignedAssistants}
            assignAssistant={assignAssistant}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            clientAssistants={clientAssistants}
            loadingClientAssistants={loadingClientAssistants}
          />
      }
    </>
  )
}

function UserAdministrationNewContainer(props) {
  const { t } = useTranslation();
  const { setShowError } = useContext(Context);
  return (
    <UserAdministrationNewAction {...props} t={t} setShowError={setShowError} />
  );
}

export { UserAdministrationNewContainer as default };
